/**
 * 법인 주소(/c/:tenant/…) 라우팅 부품 — 2026-10-20 1단계(D-01).
 *
 *   /                    → SpaceIndexRedirect : 첫 화면 설정(users/{uid}/preferences/home)이 가리키는 홈(/my 또는 /c/{tenant}/{biz})
 *   /c/:tenant/…         → TenantGate         : 화이트리스트·소속 확인. 아니면 기본 공간 홈으로
 *   /c/:tenant           → TenantHomeRedirect : 기본 사업 홈 /c/{tenant}/{biz}
 *   /c/:tenant/:biz/…    → BizGate            : 그 법인 사업인지 확인. 아니면 :biz 자리를 메뉴로 보고 기본 사업 아래로.
 *                                              사업 권한(BIZ_META.gate, 석산 = aggregate.view)이 없으면 기본 사업 홈으로.
 *                                              그 사업에 없는 메뉴(석산의 홈·레미콘 메뉴)면 그 사업 첫 메뉴로(2026-10-29)
 *   /sales 등 옛 주소    → LegacyTenantRedirect: menuPath(key, 중앙) 아래로 (쿼리·해시 유지 — 북마크·알림·푸시 딥링크 보호)
 *   /me…                 → LegacyMyRedirect   : /my… (2026-10-22 D-26)
 *   사업 안 별칭         → TenantNavigate     : …/material → …/raw-materials?tab=prices 등
 *
 * 모두 replace 이동 — 리다이렉트 단계가 뒤로가기 기록에 남지 않는다.
 * 컴포넌트만 둔다(Fast Refresh). 공간 판정 규칙은 domain/space.ts, 상태는 contexts/SpaceContext.tsx.
 */
import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useHomePref } from "@/hooks/useHomePref";
import { useSpace } from "@/hooks/useSpace";
import { useToast } from "@/hooks/useToast";
import { resolveHomePath } from "@/domain/home_pref";
import {
  BIZ_META,
  LEGACY_TENANT,
  SPACE_META,
  bizGate,
  bizHomePath,
  defaultBiz,
  isTenantKey,
  spaceHomePath,
  tenantHasBiz,
} from "@/domain/space";
import { bizHasMenu, findMenu, firstBizMenuKey, menuKeyOfPath, menuPath } from "@/domain/menus";

/**
 * "/" — 로그인 직후·앱을 새로 열면(홈 화면 앱 start_url 포함) 첫 화면 설정으로(2026-11-02, 내 계정·환경 › 첫 화면).
 * 설정 없음 = 내 업무(사용자 결정 10-26). 열 수 없는 법인 홈 설정은 내 업무로(domain/home_pref.ts). ?mock= 등 쿼리는 유지.
 * 설정을 읽기 전에는 그리지 않는다 — 내 업무로 갔다가 법인 홈으로 다시 튀는 깜빡임 방지(SpaceProvider 가 소속 확인 전 안 그리는 것과 같음).
 */
export function SpaceIndexRedirect() {
  const { search, hash } = useLocation();
  const { pref, loaded } = useHomePref();
  const { tenants, myBizs, hasPermIn, defaultSpace } = useSpace();
  if (!loaded) return null;
  const home = resolveHomePath(pref, { tenants, myBizs, hasPermIn, defaultSpace });
  return <Navigate to={`${home}${search}${hash}`} replace />;
}

/** /c/:tenant 게이트 — 없는 법인 값이거나 소속이 아니면 기본 공간 홈으로 */
export function TenantGate() {
  const { tenant } = useParams();
  const { tenants, defaultSpace } = useSpace();
  const toast = useToast();
  const { search } = useLocation();
  const ok = isTenantKey(tenant) && tenants.includes(tenant);
  const notified = useRef(false);
  useEffect(() => {
    if (ok || notified.current) return;
    notified.current = true;
    // 소속 아닌 법인(경원 관리자가 중앙 링크를 연 경우 등)만 안내. 오타 주소는 조용히 돌린다.
    if (isTenantKey(tenant)) toast.info(`${SPACE_META[tenant].label} 업무 공간 권한이 없어 기본 화면으로 이동했습니다.`);
  }, [ok, tenant, toast]);
  if (!ok) {
    // ?mock= 은 유지(미리보기 소속 전환 직후 리다이렉트돼도 같은 가상 소속)
    const mock = new URLSearchParams(search).get("mock");
    return <Navigate to={`${spaceHomePath(defaultSpace)}${mock ? `?mock=${mock}` : ""}`} replace />;
  }
  return <Outlet />;
}

/** /c/:tenant — 기본 사업 홈으로 (쿼리·해시 유지) */
export function TenantHomeRedirect() {
  const { tenant } = useParams();
  const { search, hash } = useLocation();
  const t = isTenantKey(tenant) ? tenant : LEGACY_TENANT;
  return <Navigate to={`${bizHomePath(t)}${search}${hash}`} replace />;
}

/**
 * /c/:tenant/:biz 게이트 — 그 법인의 사업이면 통과. 아니면 :biz 자리를 메뉴 이름으로 보고
 * 기본 사업 아래로 보낸다(/c/joongang/sales?tab=x → /c/joongang/rmc/sales?tab=x — 1단계 주소 북마크 호환).
 *
 * 2026-10-29: 사업 권한(BIZ_META.gate — 석산 `aggregate.view`)이 없으면 안내 후 기본 사업 홈으로.
 * 그 사업에 없는 메뉴(석산의 대시보드·레미콘 메뉴)면 그 사업 첫 메뉴로 — 중앙 레미콘 자료가 석산 주소로 보이지 않게.
 * 등록 안 된 key(material·route 별칭, 없는 주소)는 그대로 통과시켜 하위 라우트(리다이렉트·404)가 처리한다.
 */
export function BizGate() {
  const { tenant, biz } = useParams();
  const { pathname, search, hash } = useLocation();
  const { hasPermIn } = useSpace();
  const toast = useToast();
  const t = isTenantKey(tenant) ? tenant : LEGACY_TENANT;
  const valid = tenantHasBiz(t, biz);
  const gate = valid ? bizGate(biz) : undefined;
  const allowed = valid && (!gate || hasPermIn(gate, t));
  const bizLabel = valid ? BIZ_META[biz].label : "";
  const notified = useRef(false);
  useEffect(() => {
    if (!valid || allowed || notified.current) return;
    notified.current = true;
    toast.info(`${bizLabel} 사업 권한이 없어 ${SPACE_META[t].short} 기본 화면으로 이동했습니다.`);
  }, [valid, allowed, bizLabel, t, toast]);
  if (!valid) {
    const rest = pathname.split("/").filter(Boolean).slice(2).join("/");
    return <Navigate to={`${bizHomePath(t, defaultBiz(t))}/${rest}${search}${hash}`} replace />;
  }
  if (!allowed) return <Navigate to={bizHomePath(t, defaultBiz(t))} replace />;
  const key = menuKeyOfPath(pathname);
  if (findMenu(key) && !bizHasMenu(t, biz, key)) {
    const first = firstBizMenuKey(t, biz);
    // 첫 메뉴가 없는 사업(있을 수 없지만 무한 이동 방지)은 그대로 통과
    if (first && first !== key) return <Navigate to={menuPath(first, t, biz)} replace />;
  }
  return <Outlet />;
}

/** 옛 주소 → menuPath(첫 세그먼트, 중앙) 아래로(나머지 경로·쿼리·해시 그대로). /dashboard 는 사업 홈 */
export function LegacyTenantRedirect() {
  const { pathname, search, hash } = useLocation();
  const segs = pathname.split("/").filter(Boolean);
  const key = segs[0] === "dashboard" ? "dashboard" : segs[0];
  const tail = segs.slice(1).join("/");
  const base = menuPath(key, LEGACY_TENANT);
  return <Navigate to={`${base}${tail ? `/${tail}` : ""}${search}${hash}`} replace />;
}

/** 옛 내 업무 주소 /me… → /my… */
export function LegacyMyRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/me(?=\/|$)/, "/my")}${search}${hash}`} replace />;
}

/** 지금 사업 안의 다른 메뉴로 (to = "raw-materials?tab=prices" 처럼 접두어 없이) */
export function TenantNavigate({ to }: { to: string }) {
  const { tenant, biz } = useParams();
  const t = isTenantKey(tenant) ? tenant : LEGACY_TENANT;
  const home = bizHomePath(t, tenantHasBiz(t, biz) ? biz : defaultBiz(t));
  return <Navigate to={`${home}${to ? `/${to}` : ""}`} replace />;
}
